/**
 * Sign-in, without the pixels.
 *
 * The screen at /login is two steps: a work email, then the six-digit code
 * the Worker emails to it. Everything that decides what the screen shows,
 * what it sends and where it lands afterwards is a pure function here, so
 * `tests/login.test.ts` can walk every path without a DOM. The page in
 * pages/Login.tsx renders this state and calls lib/session.ts.
 *
 * ── THE ONE THING THE COPY MUST NOT SAY ──────────────────────────────────
 * The Worker answers `auth/start` with 204 for every address, known or not
 * (routes/session.ts: an address list for a university's administrators is
 * worth having, so the response never reveals one). The screen therefore
 * cannot say "we sent you a code". It says a code is on its way IF the
 * address is on an institution's team, and it tells the reader what to do
 * when nothing arrives. That sentence is not hedging; it is the truth of
 * what just happened.
 *
 * ── WHY THE CODE IS A TYPED CODE AND NOT A LINK ──────────────────────────
 * lib/email.ts explains it: university mail security pre-fetches inbound
 * links and burns a one-time link before the human clicks it. The code
 * survives that, and it travels in the subject line so it can be read off a
 * phone notification.
 */

import { matchRoute } from "~/app/router";
import type { ApiError } from "~/app/lib/session";

/* ==========================================================================
   1. Shapes
   ========================================================================== */

export type LoginStep = "email" | "code";

/** What the screen is waiting on. `idle` is the only state that accepts input. */
export type LoginPhase = "idle" | "sending" | "verifying" | "done";

export interface LoginState {
  readonly step: LoginStep;
  readonly email: string;
  readonly code: string;
  readonly phase: LoginPhase;
  /** A sentence to show beside the form. Cleared by the next edit. */
  readonly error: string | null;
  /** True once a second code was requested from the code step. */
  readonly resent: boolean;
}

export type LoginEvent =
  | { readonly type: "edit-email"; readonly value: string }
  | { readonly type: "edit-code"; readonly value: string }
  | { readonly type: "send" }
  | { readonly type: "sent" }
  | { readonly type: "send-failed"; readonly message: string }
  | { readonly type: "verify" }
  | { readonly type: "verified" }
  | { readonly type: "verify-failed"; readonly message: string; readonly clearCode: boolean }
  | { readonly type: "resend" }
  | { readonly type: "change-email" };

export const INITIAL_STATE: LoginState = {
  step: "email",
  email: "",
  code: "",
  phase: "idle",
  error: null,
  resent: false,
};

/* ==========================================================================
   2. Input rules

   The email rule is the Worker's own (routes/session.ts `EMAIL`), so the
   screen refuses exactly what the Worker would refuse and nothing else.
   ========================================================================== */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const CODE_LENGTH = 6;

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function isEmail(value: string): boolean {
  return EMAIL.test(normalizeEmail(value));
}

/**
 * Digits only, at most six. A code pasted as "123 456" or typed with a
 * hyphen from the subject line is still the code.
 */
export function normalizeCode(value: string): string {
  return value.replace(/\D+/g, "").slice(0, CODE_LENGTH);
}

export function isCode(value: string): boolean {
  return normalizeCode(value).length === CODE_LENGTH;
}

/* ==========================================================================
   3. The reducer
   ========================================================================== */

export function reduce(state: LoginState, event: LoginEvent): LoginState {
  switch (event.type) {
    case "edit-email":
      if (state.phase !== "idle") return state;
      return { ...state, email: event.value, error: null };

    case "edit-code":
      if (state.phase !== "idle") return state;
      return { ...state, code: normalizeCode(event.value), error: null };

    case "send": {
      if (state.phase !== "idle" || state.step !== "email") return state;
      if (!isEmail(state.email)) return { ...state, error: COPY.emailInvalid };
      return { ...state, email: normalizeEmail(state.email), phase: "sending", error: null };
    }

    case "sent":
      if (state.phase !== "sending") return state;
      return { ...state, step: "code", phase: "idle", code: "", error: null };

    case "send-failed":
      if (state.phase !== "sending") return state;
      return { ...state, phase: "idle", error: event.message };

    case "verify": {
      if (state.phase !== "idle" || state.step !== "code") return state;
      if (!isCode(state.code)) return { ...state, error: COPY.codeIncomplete };
      return { ...state, phase: "verifying", error: null };
    }

    case "verified":
      if (state.phase !== "verifying") return state;
      return { ...state, phase: "done", error: null };

    case "verify-failed":
      if (state.phase !== "verifying") return state;
      return {
        ...state,
        phase: "idle",
        error: event.message,
        code: event.clearCode ? "" : state.code,
      };

    case "resend": {
      // Back through the sending phase with the same address, so the
      // `sent` and `send-failed` events land the same way they did the
      // first time. The step stays on the code, because a reader who asked
      // for a new code is looking at their inbox, not at the email field.
      if (state.phase !== "idle" || state.step !== "code") return state;
      return { ...state, phase: "sending", code: "", error: null, resent: true };
    }

    case "change-email":
      if (state.phase !== "idle") return state;
      return { ...INITIAL_STATE, email: state.email };
  }
}

/**
 * `sent` from the code step (a resend) must land on the code step, and
 * `sent` from the email step must move to it. Both are the one `sent` case
 * above: it sets `step: "code"` either way, which is correct for both.
 */

/* ==========================================================================
   4. Failures, as sentences

   The Worker's `message` is written to be shown verbatim (lib/http.ts), so
   it is. The two cases the screen handles differently are the ones where
   the code the reader typed is gone for good: an expired code (410) and a
   code refused too many times (429). Both clear the field and point at the
   new-code button, because typing the same six digits again cannot work.
   ========================================================================== */

export interface Failure {
  readonly message: string;
  /** True when the code the reader has is no longer usable. */
  readonly clearCode: boolean;
  /** True when the deployment cannot sign anyone in (no session pepper). */
  readonly unconfigured: boolean;
}

export function describeVerifyFailure(status: number | null, error: ApiError): Failure {
  const unconfigured = status === 503 && error.code === "AUTH_UNCONFIGURED";
  const gone = status === 410 || status === 429;
  return { message: error.message, clearCode: gone, unconfigured };
}

export function describeSendFailure(status: number | null, error: ApiError): Failure {
  const unconfigured = status === 503 && error.code === "AUTH_UNCONFIGURED";
  return { message: error.message, clearCode: false, unconfigured };
}

/* ==========================================================================
   5. Where to land

   `/login?next=/admin/billing` comes from the session guard, which sends a
   signed-out visitor here from the screen they asked for. The value is
   trusted only if it is one of this app's own signed-in routes: an absolute
   URL, a protocol-relative one, or any other page on this host is ignored
   and the reader lands on /admin. That is the whole open-redirect defence,
   and it reads the route table rather than a prefix so a future signed-in
   route joins it by being declared, not by being remembered here.
   ========================================================================== */

export const DEFAULT_LANDING = "/admin";

const SIGNED_IN_KINDS: ReadonlySet<string> = new Set([
  "admin",
  "admin-settings",
  "admin-billing",
  "admin-team",
]);

export function landingFrom(search: string): string {
  const next = new URLSearchParams(search).get("next");
  if (!next) return DEFAULT_LANDING;
  if (!next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return DEFAULT_LANDING;
  // Whitespace or a control character anywhere is a header-injection shape, not a path.
  for (const ch of next) {
    const code = ch.charCodeAt(0);
    if (code <= 32 || code === 127) return DEFAULT_LANDING;
  }
  const queryAt = next.search(/[?#]/);
  const pathname = queryAt === -1 ? next : next.slice(0, queryAt);
  return SIGNED_IN_KINDS.has(matchRoute(pathname).kind) ? next : DEFAULT_LANDING;
}

/** The address the guard sends a signed-out visitor to, carrying where they were. */
export function loginPathFor(currentHref: string): string {
  const at = currentHref.search(/[?#]/);
  const pathname = at === -1 ? currentHref : currentHref.slice(0, at);
  if (!SIGNED_IN_KINDS.has(matchRoute(pathname).kind)) return "/login";
  return `/login?next=${encodeURIComponent(currentHref)}`;
}

/* ==========================================================================
   6. Copy

   Standing rules: US spelling, no em dashes, "students" and never the other
   word, no claim the product cannot keep. A test reads every string here.
   ========================================================================== */

export const COPY = {
  /* The member app's greeting, so the two sign-ins read as one family
     (Thomas, Sep 9 2026 night); the lede says whose dashboard this is. */
  welcome: "Welcome back",
  welcomeLede: "Sign in to your institution's dashboard.",
  emailLabel: "Work email",
  emailPlaceholder: "name@institution.edu",
  emailInvalid: "Enter your work email address.",
  send: "Send code",
  sending: "Sending",

  codeTitle: "Check your email",
  /** The truthful sentence. See the file header. */
  codeLede: (email: string) =>
    `If ${email} is on an institution's team, a six-digit code is on its way from P3 Partners. It is in the subject line and good for 10 minutes.`,
  codeNothing:
    "Nothing there? Check spam, then ask an owner at your institution to add you under Team and roles.",
  codeLabel: "Six-digit code",
  codeIncomplete: "Enter the six digits from the message.",
  verify: "Sign in",
  verifying: "Checking",
  resend: "Send a new code",
  resent: "A new code is on its way, if the address is on a team.",
  changeEmail: "Use a different email",

  /* The member app's maroon "New to P3?" card, pointed at this host's
     registration: the path a new visitor needs, as its own zone rather
     than a second crimson button under Sign in. */
  promoTitle: "New to P3 Enterprise?",
  promoText: "Register your institution online in a few minutes.",
  promoCta: "Register your institution",
  studentLine: "A student?",
  studentLink: "Open the app",
  unconfiguredTitle: "Sign-in is not available on this deployment",
} as const;
